"use client";

import { AppBar, CircularProgress, Tab, Alert } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Form, Formik, FormikHelpers } from "formik";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Tabs from "@/components/atoms/ZoomAwareTabs";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import { ConfirmationDialog } from "@/components/molecules/ConfirmationDialog";
import {
  CampaignFormData,
  CampaignTabs,
  CampaignWithFormData,
} from "@/components/screens/CampaignFormPage/types";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useTabs from "@/hooks/use-tabs";
import useGetCustomerId from "@/hooks/use-get-customer-id";
import { ICampaignTemplate } from "@/interfaces/campaign-template.interfaces";
import { ICampaign } from "@/interfaces/campaign.interface";
import { CampaignFormPageMessages } from "@/messages/campaigns/campaign-form-page.messages";

import CampaignDataTab from "./components/CampaignDataTab";
import ContactsDataTab from "./components/ContactsDataTab";
import ProductsDataTab from "./components/ProductsDataTab";
import StatisticTab from "./components/StatisticDataTab";
import { TABS_ITEMS } from "./constants";
import { CampaignType } from "@/types/campaign.types";
import SimpleMessageDataTab from "./components/SimpleMessageDataTab";
import { buildFormSchema, buildInitialData } from "./form-data";
import CampaignTypeObserver from "./components/CampaignTypeObserver";

type Props = {
  onSubmit: (data: CampaignFormData) => void;
  campaign: CampaignWithFormData | ICampaign | null;
  isEditing: boolean;
  messages: CampaignFormPageMessages;
  isLoading: boolean;
  template?: ICampaignTemplate;
  setTemplateIndex: React.Dispatch<React.SetStateAction<number>>;
};

const CampaignForm = ({
  campaign,
  isEditing,
  messages,
  onSubmit,
  isLoading,
  template,
  setTemplateIndex,
}: Props) => {
  const router = useNavigate();
  const { lang } = useLanguage();
  const customerId = useGetCustomerId();

  const [noFiltersDialogOpen, setNoFiltersDialogOpen] = useState(false);
  const [noProductsDialogOpen, setNoProductsDialogOpen] = useState(false);
  const [noSchedulesError, setNoSchedulesError] = useState(false);
  const [activeTab, setActiveTab] = useState<CampaignTabs>("campaignDataTab");

  const [currentCampaignType, setCurrentCampaignType] = useState(campaign?.type);
  const TABS = TABS_ITEMS(messages, currentCampaignType);
  const tabs = useTabs<CampaignTabs>({
    initialTab: "campaignDataTab",
    tabs: TABS,
    isEditing,
  });

  // Override tab state with our local state
  const effectiveTabs = {
    ...tabs,
    activeTab,
    changeTab: (newTab: CampaignTabs) => {
      setActiveTab(newTab);
    },
  };

  // When editing any campaign, always start with campaign data tab
  useEffect(() => {
    if (isEditing && campaign?.type !== undefined) {
      setCurrentCampaignType(campaign.type);
      // Always start with campaignDataTab when editing
      setActiveTab("campaignDataTab");
    }
  }, [isEditing, campaign?.type]);

  const handleTypeChange = (type: CampaignType) => {
    if (currentCampaignType !== type) {
      setCurrentCampaignType(type);
      // Reset to first tab when type changes
      effectiveTabs.changeTab("campaignDataTab");
    }
  };

  const isCampaignTab = effectiveTabs.activeTab === "campaignDataTab";
  const isContactsDataTab = effectiveTabs.activeTab === "contactsDataTab";
  const isProductsDataTab = effectiveTabs.activeTab === "productsDataTab";
  const isSimpleMessageDataTab = effectiveTabs.activeTab === "simpleMessageDataTab";
  const isStatisticsDataTab = effectiveTabs.activeTab === "statisticsDataTab";

  const steps: CampaignTabs[] =
    currentCampaignType === CampaignType.SimpleMessage
      ? ["campaignDataTab", "contactsDataTab", "simpleMessageDataTab", "statisticsDataTab"]
      : ["campaignDataTab", "contactsDataTab", "productsDataTab", "statisticsDataTab"];

  const hasNoFilters = (values: CampaignFormData) => {
    const { birthdayPeriod, gender, contactSource, minimumAge, maximumAge, cities, contactTag } =
      values.segment;

    const noFilterApplied =
      !birthdayPeriod &&
      !gender &&
      !contactSource &&
      (minimumAge === null || minimumAge === undefined || minimumAge === "") &&
      (maximumAge === null || maximumAge === undefined || maximumAge === "") &&
      (cities?.length ?? 0) === 0 &&
      (contactTag?.length ?? 0) === 0;

    return noFilterApplied;
  };

  const hasNoProducts = (values: CampaignFormData) => values.items.length === 0;

  const hasNoSimpleMessage = (values: CampaignFormData) => {
    if (values.type !== CampaignType.SimpleMessage) return false;
    return !values.simpleMessage || values.simpleMessage.trim().length === 0;
  };

  const handleSubmitForm = async (
    values: CampaignFormData,
    actions: FormikHelpers<CampaignFormData>
  ) => {
    // Limpar erro anterior
    setNoSchedulesError(false);

    // Validação manual baseada na aba atual
    if (isCampaignTab) {
      if (!values.isImmediateScheduling && (!values.schedules || values.schedules.length === 0)) {
        setNoSchedulesError(true);
        actions.setSubmitting(false);
        return;
      }
      if (!values.name || !values.code) {
        actions.setSubmitting(false);
        return;
      }
    }

    if (isEditing) {
      if (hasNoFilters(values)) {
        setNoFiltersDialogOpen(true);
      } else if (hasNoProducts(values) && values.type !== CampaignType.SimpleMessage) {
        setNoProductsDialogOpen(true);
      } else {
        onSubmit(values);
      }
      actions.setSubmitting(false);
      return;
    }

    const currentStepIndex = steps.indexOf(effectiveTabs.activeTab);

    // Se não está na última aba, navega para a próxima
    if (!isProductsDataTab && !isSimpleMessageDataTab) {
      if (isContactsDataTab && hasNoFilters(values)) {
        setNoFiltersDialogOpen(true);
        actions.setSubmitting(false);
        return;
      }

      // Move to next step
      const nextStep = steps[currentStepIndex + 1];
      effectiveTabs.changeTab(nextStep);
      actions.setTouched({});
      actions.setSubmitting(false);
      return;
    }

    // Se está na última aba, valida e submete
    if (isProductsDataTab) {
      if (hasNoProducts(values)) {
        setNoProductsDialogOpen(true);
        actions.setSubmitting(false);
        return;
      }
      onSubmit(values);
      actions.setSubmitting(false);
      return;
    }

    if (isSimpleMessageDataTab) {
      if (hasNoSimpleMessage(values)) {
        actions.setSubmitting(false);
        return;
      }
      onSubmit(values);
      actions.setSubmitting(false);
      return;
    }

    const nextStep = steps[currentStepIndex + 1];
    tabs.changeTab(nextStep);
    actions.setTouched({});
    actions.setSubmitting(false);
  };

  const handleBackStep = () => {
    const currentStepIndex = steps.indexOf(effectiveTabs.activeTab);
    if (currentStepIndex > 0) {
      const prevStep = steps[currentStepIndex - 1];
      effectiveTabs.changeTab(prevStep);
    }
  };

  const handleNoFiltersConfirm = (
    actions: FormikHelpers<CampaignFormData>,
    values: CampaignFormData
  ) => {
    setNoFiltersDialogOpen(false);
    if (isEditing && hasNoProducts(values)) {
      setNoProductsDialogOpen(true);
    } else if (isEditing) {
      onSubmit(values);
    } else if (!isEditing) {
      const currentStepIndex = steps.indexOf(effectiveTabs.activeTab);
      const nextStep = steps[currentStepIndex + 1];
      effectiveTabs.changeTab(nextStep);
      actions.setTouched({});
      setNoFiltersDialogOpen(false);
    }
  };

  const handleNoProductsConfirm = (values: CampaignFormData) => {
    setNoProductsDialogOpen(false);
    onSubmit(values);
  };

  const handleNoFiltersCancel = () => {
    setNoFiltersDialogOpen(false);
  };

  const handleNoProductsCancel = () => {
    setNoProductsDialogOpen(false);
  };

  const handleCancelAction = () => {
    if (isEditing) {
      router(-1);
    } else {
      router(ROUTE_URLS.campaigns(lang));
    }
  };

  if (!template) {
    return <CircularProgress />;
  }

  const currentStepIndex = steps.indexOf(effectiveTabs.activeTab);

  const isStepTabDisabled = (tabValue: CampaignTabs) => {
    if (isEditing) return false;
    const tabIndex = steps.indexOf(tabValue);
    return tabIndex === -1 || tabIndex > currentStepIndex;
  };

  const isLastTab = isProductsDataTab || isSimpleMessageDataTab;
  const submitButton = isEditing
    ? messages.updateButton
    : isLastTab
      ? messages.updateButton
      : messages.continueButton;
  const isCancelAction = isEditing || (isCampaignTab && !isEditing);
  const cancelOrBackButton = isCancelAction ? messages.cancelButton : messages.backButton;

  return (
    <Formik
      initialValues={buildInitialData(
        template,
        isEditing,
        campaign as ICampaign | null,
        Number(customerId) || undefined
      )}
      validationSchema={buildFormSchema(0, messages)}
      onSubmit={(values, actions) => {
        handleSubmitForm(values, actions);
      }}
      validateOnMount={false}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {(formikProps) => (
        <Form>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 12, lg: 5 }}>
              <AppBar position="static">
                <Tabs
                  orientation={effectiveTabs.tabsOrientation}
                  value={effectiveTabs.activeTab}
                  onChange={(_, newTab) => {
                    if (!isStepTabDisabled(newTab)) effectiveTabs.changeTab(newTab);
                  }}
                >
                  {TABS.map((tab, index) => (
                    <Tab
                      key={index}
                      label={tab.label}
                      value={tab.value}
                      disabled={isStepTabDisabled(tab.value)}
                    />
                  ))}
                </Tabs>
              </AppBar>
            </Grid>
          </Grid>

          <CampaignTypeObserver onTypeChange={handleTypeChange} />

          {isCampaignTab && (
            <CampaignDataTab messages={messages} setTIndex={setTemplateIndex} template={template} />
          )}

          {isContactsDataTab && <ContactsDataTab messages={messages} />}
          {isProductsDataTab && <ProductsDataTab messages={messages} />}
          {isSimpleMessageDataTab && <SimpleMessageDataTab messages={messages} />}
          {isStatisticsDataTab && <StatisticTab messages={messages} />}

          {effectiveTabs.activeTab !== "statisticsDataTab" && (
            <MDBox mt={2}>
              {noSchedulesError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  É necessário configurar pelo menos uma data e horário de agendamento para criar a
                  campanha.
                </Alert>
              )}
              <MDBox display="flex" justifyContent="flex-end" gap={2}>
                <MDButton
                  variant="gradient"
                  color="light"
                  onClick={isCancelAction ? handleCancelAction : handleBackStep}
                  disabled={isLoading}
                >
                  {cancelOrBackButton}
                </MDButton>

                <MDButton
                  variant="gradient"
                  type="submit"
                  color="dark"
                  isLoading={isLoading}
                  disabled={
                    (formikProps.values.disabledField && !isCampaignTab) ||
                    isLoading ||
                    formikProps.values.isHoldStatus ||
                    formikProps.values.isConfirmedStatus ||
                    (isProductsDataTab && formikProps.values.isActiveStatus)
                  }
                  onClick={() => {}}
                >
                  {submitButton}
                </MDButton>
              </MDBox>
            </MDBox>
          )}

          <ConfirmationDialog
            isOpen={noFiltersDialogOpen}
            onClose={handleNoFiltersCancel}
            onConfirm={() => handleNoFiltersConfirm(formikProps, formikProps.values)}
            titleText={messages.noFiltersDialogTitle}
            descriptionText={messages.noFiltersDialogDescription}
            cancelText={messages.noFiltersDialogCancel}
            confirmText={messages.noFiltersDialogConfirm}
            isLoading={isLoading}
          />

          <ConfirmationDialog
            isOpen={noProductsDialogOpen}
            onClose={handleNoProductsCancel}
            onConfirm={() => handleNoProductsConfirm(formikProps.values)}
            titleText={messages.noProductsDialogTitle}
            descriptionText={messages.noProductsDialogDescription}
            cancelText={messages.noProductsDialogCancel}
            confirmText={messages.noProductsDialogConfirm}
            isLoading={isLoading}
          />

        </Form>
      )}
    </Formik>
  );
};

export default CampaignForm;
